import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { _get } from "../../services/apiClient";
import { useCart } from "../../CartContext";
import { formatCurrency } from "../../helper/currency";
import Slider from "react-slick";
import { Galleria } from "primereact/galleria";
import { FaShareAlt, FaWhatsapp } from "react-icons/fa";
import ZoomImage from "../../helper/zoomin/zoomimage";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "./productDetail.css";



function ProductDetail({ cart, setCart }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [images, setImages] = useState([]);
  const [showFullDetails, setShowFullDetails] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const { itemQuantities, updateQuantity } = useCart();
  const quantity = product ? itemQuantities[product._id]?.quantity || 0 : 0;

  const CHARACTER_LIMIT = 150;

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await _get(`/products/${id}`);
        setProduct(response.data);
        if (response.data.images?.length > 0) {
          const formatted = response.data.images.map((img) => ({
            itemImageSrc: `${process.env.REACT_APP_IMAGE_BASE_URL}${img}`,
            thumbnailImageSrc: `${process.env.REACT_APP_IMAGE_BASE_URL}${img}`,
            alt: response.data.productName,
          }));
          setImages(formatted);
        }
      } catch (err) {
        console.error("Error fetching product:", err);
      }
    };
    fetchProduct();
  }, [id]);

  if (!product) return <div>Loading...</div>;

  const handleAddToCart = () => {
    if (!cart.some((item) => item._id === product._id)) {
      setCart([...cart, product]);
    }
    updateQuantity(product._id, quantity + 1, product.variants[0].price);
  };

  const handleIncreaseQuantity = () => {
    if (!cart.some((item) => item._id === product._id)) {
      setCart([...cart, product]);
    }
    updateQuantity(product._id, quantity + 1, product.variants[0].price);
  };

  const handleDecreaseQuantity = () => {
    if (quantity > 0) {
      updateQuantity(product._id, quantity - 1, product.variants[0].price);
      if (quantity - 1 === 0) {
        setCart(cart.filter((item) => item._id !== product._id));
      }
    }
  };

  const handleGoBack = () => navigate(-1);

  const handleShare = async () => {
    const shareData = {
      title: product.productName,
      text: `Check out this product: ${product.productName}`,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log("Share canceled or failed", err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        // console.log("✅ Link copied!");
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2000);
      } catch (error) {
        console.error("❌ Copy failed:", error);
      }
    }
  };
  const handleWhatsAppShare = () => {
    const message = `Check out this product: ${product.productName} - ${window.location.href}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };


  const carouselSettings = {
    dots: true,
    infinite: false,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
  };

  const itemTemplate = (item) => (
    <ZoomImage
      src={item.itemImageSrc}
      alt={item.alt}
    />
  );

  const thumbnailTemplate = (item) => (
    <img
      src={item.thumbnailImageSrc}
      alt={item.alt}
      style={{
        width: "80px",
        height: "80px",
        objectFit: "cover",
        borderRadius: "4px",
      }}
    />
  );

  const renderProductDetails = (details) => {
    if (!details) return "No details available";

    if (details.length <= CHARACTER_LIMIT) return details;

    if (showFullDetails) {
      return (
        <>
          {details}{" "}
          <span
            className="read-more-link"
            onClick={() => setShowFullDetails(false)}
            style={{ color: "blue", cursor: "pointer" }}
          >
            Read Less
          </span>
        </>
      );
    }

    return (
      <>
        {details.substring(0, CHARACTER_LIMIT)}...{" "}
        <span
          className="read-more-link"
          onClick={() => setShowFullDetails(true)}
          style={{ color: "blue", cursor: "pointer" }}
        >
          Read More
        </span>
      </>
    );
  };

  return (
    <div className="product-detail-container">
      <div className="back-button">
        <button className="btn btn-outline-secondary" onClick={handleGoBack}>
          &#8592; Back
        </button>
      </div>

      {/* Desktop Layout */}
      <div className="desktop-only">
        <div className="left-panel">
          <Galleria
            value={images}
            numVisible={4}
            style={{ maxWidth: "600px" }}
            circular
            showThumbnails
            showItemNavigators
            item={itemTemplate}
            thumbnail={thumbnailTemplate}
            thumbnailsPosition="left"
          />
        </div>

        <div className="right-panel">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <h3>{product.productName}</h3>
            <button className="share-button" onClick={handleShare}>
              <FaShareAlt className="share-icon" />
              Share
            </button>
          </div>

          <p>
            <strong>Price: </strong>
            {product.variants?.[0]?.offerPrice ? (
              <>
                {(() => {
                  const originalPrice = product.variants[0].price;
                  const offerPrice = product.variants[0].offerPrice;
                  const discount = Math.round(
                    ((originalPrice - offerPrice) / originalPrice) * 100
                  );
                  return (
                    <>
                      <span className="original-price">
                        {formatCurrency(originalPrice)}
                      </span>{" "}
                      <span className="offer-price">
                        {formatCurrency(offerPrice)}
                      </span>
                      <span className="discount-badge">{discount}% OFF</span>
                    </>
                  );
                })()}
              </>
            ) : (
              formatCurrency(product.variants?.[0]?.price)
            )}
          </p>

          <p>
            <strong>Manufacture Date: </strong>
            {product.variants?.[0]?.manufactureDate
              ? new Date(product.variants[0].manufactureDate).toDateString()
              : "N/A"}
          </p>
          <p>
            <strong>Expiry Date: </strong>
            {product.variants?.[0]?.expiryDate
              ? new Date(product.variants[0].expiryDate).toDateString()
              : "N/A"}
          </p>

          <div className="mt-3">
            {quantity > 0 ? (
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleDecreaseQuantity}
                >
                  -
                </button>
                <span className="btn btn-outline-secondary">{quantity}</span>
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleIncreaseQuantity}
                >
                  +
                </button>
              </div>
            ) : (
              <button
                className="btn btn-danger fw-bold"
                onClick={handleAddToCart}
              >
                Add to Cart
              </button>
            )}
          </div>

          {/* Product Overview Section */}
          <div className="product-overview-section mt-4">
            <h4 className="section-title">Product Overview</h4>

            <div className="overview-block">
              <h6>Product Details</h6>
              <p>{renderProductDetails(product.productDetails)}</p>
            </div>

            <div className="overview-block">
              <h6>Key Features</h6>
              <div
                className="key-features"
                dangerouslySetInnerHTML={{
                  __html:
                    product.keyFeatures || "<p>No key features listed.</p>",
                }}
              ></div>
            </div>

            {product.productSpecifications?.length > 0 && (
              <div className="overview-block">
                <h6>Product Specifications</h6>
                <table className="spec-table">
                  <tbody>
                    {product.productSpecifications.map((spec, idx) => (
                      <tr key={idx}>
                        <td className="spec-key">{spec.key}</td>
                        <td className="spec-value">{spec.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="mobile-only">
        <div style={{ position: "relative" }}>
          <Slider {...carouselSettings} className="mobile-carousel">
            {product.images?.map((img, idx) => (
              <div key={idx}>
                <img
                  src={`${process.env.REACT_APP_IMAGE_BASE_URL}${img}`}
                  alt={`slide-${idx}`}
                />
              </div>
            ))}
          </Slider>
          <button className="mobile-share-button" onClick={handleShare}>
            <FaShareAlt />
          </button>
          <button className="mobile-whatsapp-button" onClick={handleWhatsAppShare}>
            <FaWhatsapp style={{ color: "#25D366", fontSize: "20px" }} />
          </button>
        </div>

        <div className="mt-3">
          <h3>{product.productName}</h3>
          <p>
            <strong>Price: </strong>
            {product.variants?.[0]?.offerPrice ? (
              <>
                <span className="original-price">
                  {formatCurrency(product.variants[0].price)}
                </span>{" "}
                <span className="offer-price">
                  {formatCurrency(product.variants[0].offerPrice)}
                </span>
              </>
            ) : (
              formatCurrency(product.variants?.[0]?.price)
            )}
          </p>
          <p>
            <strong>Manufacture Date: </strong>
            {product.variants?.[0]?.manufactureDate
              ? new Date(product.variants[0].manufactureDate).toDateString()
              : "N/A"}
          </p>
          <p>
            <strong>Expiry Date: </strong>
            {product.variants?.[0]?.expiryDate
              ? new Date(product.variants[0].expiryDate).toDateString()
              : "N/A"}
          </p>

          <div className="mt-3">
            {quantity > 0 ? (
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleDecreaseQuantity}
                >
                  -
                </button>
                <span className="btn btn-outline-secondary">{quantity}</span>
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleIncreaseQuantity}
                >
                  +
                </button>
              </div>
            ) : (
              <button
                className="btn btn-danger fw-bold"
                onClick={handleAddToCart}
              >
                Add to Cart
              </button>
            )}
          </div>

          {/* Product Overview (Mobile) */}
          <div className="product-overview-section mt-4">
            <h4 className="section-title">Product Overview</h4>

            <div className="overview-block">
              <h6>Product Details</h6>
              <p>{renderProductDetails(product.productDetails)}</p>
            </div>

            <div className="overview-block">
              <h6>Key Features</h6>
              <div
                className="key-features"
                dangerouslySetInnerHTML={{
                  __html:
                    product.keyFeatures || "<p>No key features listed.</p>",
                }}
              ></div>
            </div>

            {product.productSpecifications?.length > 0 && (
              <div className="overview-block">
                <h6>Product Specifications</h6>
                <table className="spec-table">
                  <tbody>
                    {product.productSpecifications.map((spec, idx) => (
                      <tr key={idx}>
                        <td className="spec-key">{spec.key}</td>
                        <td className="spec-value">{spec.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toast for copy feedback */}
      <div className={`copy-toast ${showToast ? "show" : ""}`}>
        Link copied!
      </div>
    </div>
  );
}

export default ProductDetail;
